import { Button, Checkbox, DatePicker, Descriptions, Divider, Form, Input, message, Modal, Select, Space, TableProps, Tooltip, Upload } from 'antd'
import { BlockOutlined, EyeOutlined, MoneyCollectOutlined, UploadOutlined } from '@ant-design/icons'
import { useCallback, useEffect, useState } from 'react'
import { useLoading } from '../../context/LoadingContext'
import { toastError, toastSuccess } from '../../utils/toast'
import { CheckOutlined } from '@ant-design/icons'
import { apiAddTransactionConnectionByOrderId, apiGetListCompanies, apiGetListOrderTransactionConnection, apiGetListPackagesConnection, apiGetOrderTransactionConnectionDetail, apiUpdateOrderTransactionConnection, apiUploadOrderTransactionConnectionByExcel } from '../../api'
import defaultParams, { TRANSACTION_CONNECTION_TYPE_CONNECTION, TRANSACTION_CONNECTION_TYPE_EXTENSION } from '../../assets/constants/default_params'
import { ICompany } from '../../models/company.modal'
import { filterOptionByLabel } from '../../utils/filter-utils'
import * as XLSX from 'xlsx'
import CustomTable from '../../components/table'
import { IPartnerPackage } from '../../models/package.modal'
import { authSelector } from '../../redux/slices/authSlice'
import { useSelector } from 'react-redux'
import { renderDateByUnix, renderStatus, renderType } from '../kit-sim/method'
import moment from 'moment'

export default function Connect() {
    const [companyOptions, setCompanyOptions] = useState<{ label: string, value: Number }[]>([])
    const [partnerPackageOptions, setPartnerPackageOptions] = useState<{ label: string, value: Number }[]>([])
    const [fileExcel, setFileExcel] = useState<any>()
    const [dataExcel, setDataExcel] = useState<any>(null)
    const [cpCode, setCpCode] = useState<string>('')
    const [filterPack, setFilterPack] = useState<string>('')
    const [isOpenSelectDate, setIsOpenSelectDate] = useState(false)
    const [currentPagePreview, setCurrentPagePreview] = useState<number>(defaultParams.PAGE)
    const [pageSizePreview, setPageSizePreview] = useState<number>(defaultParams.MIN_LIMIT)
    const [orderAdded, setOrderAdded] = useState<any>(null)
    const [modalOrderId, setModalOrderId] = useState<{ data: any | null, isOpen: boolean }>({
        data: null,
        isOpen: false,
    })
    const [orderDetail, setOrderDetail] = useState<any>(null)
    const [currentPageDetail, setCurrentPageDetail] = useState<number>(defaultParams.PAGE)
    const [pageSizeDetail, setPageSizeDetail] = useState<number>(defaultParams.MIN_LIMIT)
    const [modalLock, setModalLock] = useState<boolean>(false)
    const { setIsLoading } = useLoading()
    const [formConnect] = Form.useForm()
    const { account } = useSelector(authSelector)

    const fetchCompanies = useCallback(async () => {
        try {
            const resp = await apiGetListCompanies({ limit: defaultParams.MAX_LIMIT })
            if (resp.data?.companies === null) {
                return
            }
            const options = resp.data.companies.map((item: ICompany) => ({
                label: item.name,
                value: item.code,
            }))
            setCompanyOptions(options)
        } catch (error) {
            console.log(error)
        }
    }, [])

    const fetchPartnerPackages = useCallback(async () => {
        if (!cpCode) return
        try {
            const resp = await apiGetListPackagesConnection(cpCode)
            if (resp.data?.partner_packages === null) {
                return
            }
            const options = resp.data.partner_packages.map((item: IPartnerPackage) => {
                let label = item.name
                let value = `${item.code}::${item.type}::${item.price}`
                if (item.type === TRANSACTION_CONNECTION_TYPE_CONNECTION) {
                    label += '_Đấu mới'
                }
                if (item.type === TRANSACTION_CONNECTION_TYPE_EXTENSION) {
                    label += '_Gia hạn'
                }
                return { label, value }
            })
            setPartnerPackageOptions(options)
        } catch (error) {
            console.log(error)
        }
    }, [cpCode])

    useEffect(() => {
        fetchCompanies()
        fetchPartnerPackages()
    }, [fetchCompanies, fetchPartnerPackages])

    const handlePreview = (values: any) => {
        const dataPack = values.dataPack.split('::')[0]
        const dataType = values.dataPack.split('::')[1]
        const startDate = values.startDate ? values.startDate.format('YYYY-MM-DD') : moment().format('YYYY-MM-DD')
        handleFileUpload(fileExcel, values.cpCode, dataPack, dataType, startDate)
    }

    const handleUploadFile = async () => {
        try {
            setIsLoading(true)
            const formData = new FormData()
            formData.append("file", fileExcel)
            const resp = await apiUploadOrderTransactionConnectionByExcel(formData, dataExcel[0].cpCode, dataExcel[0].dataPack, dataExcel[0].packType, dataExcel[0].startDate)
            if (resp.data.status !== '200') {
                toastError(resp.data.message)
                setIsLoading(false)
                return
            }
            toastSuccess('Đã tạo đơn hàng đấu nối!')
            setFileExcel(null)
            formConnect.resetFields()
            setCpCode('')
            setPartnerPackageOptions([])
            setTimeout(async () => {
                const respOrder = await apiGetListOrderTransactionConnection({ orderId: resp.data?.order?.orderId, page: 1, size: 1 })
                if (respOrder.data?.orders?.length === 1) {
                    setOrderAdded(respOrder.data.orders[0])
                } else {
                    toastError('Có lỗi xảy ra!')
                }
                setIsLoading(false)
            }, 8000);
        } catch (error) {
            console.log(error)
            setIsLoading(false)
            toastError('Có lỗi xảy ra!')
        }
    }

    const checkUploadFile = (file: any) => {
        const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || file.type === 'application/vnd.ms-excel'
        if (!isExcel) {
            message.error('Vui lòng upload file dạng excel!')
            return Upload.LIST_IGNORE
        }
        return false
    }

    const handleChangeFile = (value: any) => {
        setDataExcel(null)
        setOrderAdded(null)
        setOrderDetail(null)
        if (value.fileList?.length > 0) {
            setFileExcel(value.file)
        } else {
            setFileExcel(null)
        }
    }

    const handleFileUpload = (file: any, cpCode: string, dataPack: string, dataType: string, startDate: string) => {
        const reader = new FileReader()
        reader.onload = (event) => {
            const workbook = XLSX.read(event.target?.result, { type: 'binary' })
            const sheetName = workbook.SheetNames[0]
            const sheet = workbook.Sheets[sheetName]
            const sheetData: any = XLSX.utils.sheet_to_json(sheet)
            const firstField = Object.keys(sheetData[0])?.[0]
            const secondField = Object.keys(sheetData[0])?.[1]
            if (sheetData.length > 1000) {
                toastError('Số lượng sim tối đa là 1000!')
                setDataExcel(null)
                return
            }
            setDataExcel(sheetData.map((item: any, index: number) => ({
                index: index + 1,
                cpCode: cpCode,
                phoneSim: item[firstField]?.toString() || '',
                serialSim: item[secondField]?.toString() || '',
                dataPack: dataPack,
                packType: dataType,
                startDate: startDate,
            })))
        }
        reader.readAsBinaryString(file)
    }

    const excelColumns: TableProps<any>['columns'] = [
        {
            key: 'index',
            title: 'STT',
            dataIndex: 'index',
        },
        {
            key: 'cpCode',
            title: 'Mã công ty',
            dataIndex: 'cpCode',
        },
        {
            key: 'phoneSim',
            title: 'Msisdn',
            dataIndex: 'phoneSim',
        },
        {
            key: 'serialSim',
            title: 'Serial Sim',
            dataIndex: 'serialSim',
        },
        {
            key: 'dataPack',
            title: 'Gói cước',
            dataIndex: 'dataPack',
        },
        {
            key: 'packType',
            title: 'Loại gói cước',
            dataIndex: 'packType',
        },
        {
            key: 'startDate',
            title: 'Ngày đấu nối',
            dataIndex: 'startDate',
        },
    ]

    const handleFilterPartnerPackage = (value: string) => {
        formConnect.setFieldValue('dataPack', '')
        setCpCode(value)
    }

    const fetchOrderDetail = async () => {
        try {
            setIsLoading(true)
            const resp = await apiGetOrderTransactionConnectionDetail({
                orderId: orderAdded?.orderId,
                page: 1,
                size: defaultParams.MAX_LIMIT,
            })
            setOrderDetail(resp.data)
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }

    const handleAddTransactionConnectionByOrderId = async () => {
        try {
            setIsLoading(true)
            const resp = await apiAddTransactionConnectionByOrderId({ orderId: modalOrderId.data?.orderId })
            if (resp.status === 200) {
                toastSuccess('Đấu nối đơn hàng thành công!')
                setOrderAdded({ ...orderAdded, status: 'LOCKED' })
                handleCloseModalAddTransactionConnectionByOrderId()
            } else {
                toastError('Có lỗi xảy ra!')
            }
        } catch (error) {
            console.log(error);
            toastError('Có lỗi xảy ra!')
        } finally {
            setIsLoading(false)
        }
    }

    const handleCloseModalAddTransactionConnectionByOrderId = () => {
        setModalOrderId({ data: null, isOpen: false })
    }

    const handleLockOrderTransactionConnection = async ({ id, orderId, status }: any) => {
        try {
            setIsLoading(true)
            const resp = await apiUpdateOrderTransactionConnection({ order: { id, orderId, status } })
            if (resp.status === 200) {
                toastSuccess('Khoá đơn hàng thành công!')
                setOrderAdded({ ...orderAdded, status: 'LOCKED' })
                setModalLock(false)
            } else {
                toastError('Có lỗi xảy ra!')
            }
        } catch (error) {
            console.log(error);
            toastError('Có lỗi xảy ra!')
        } finally {
            setIsLoading(false)
        }
    }

    const orderDetailColumns: TableProps<any>['columns'] = [
        {
            key: 'phoneSim',
            title: 'Msisdn',
            dataIndex: 'phoneSim',
        },
        {
            key: 'serialSim',
            title: 'Serial Sim',
            dataIndex: 'serialSim',
        },
        {
            key: 'status',
            title: 'Trạng thái',
            dataIndex: 'status',
            render: status => renderStatus(status),
        },
        {
            key: 'description',
            title: 'Mô tả',
            dataIndex: 'description',
        },
    ]

    return (
        <div>
            <Form form={formConnect} onFinish={handlePreview}>
                <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                    <Form.Item style={{ flex: 1, minWidth: 200 }} name='cpCode' label='Công ty'
                        rules={[{ required: true, message: 'Vui lòng chọn công ty!' }]}>
                        <Select
                            disabled={dataExcel?.length > 0}
                            onChange={handleFilterPartnerPackage}
                            style={{ width: '100%' }}
                            showSearch
                            options={companyOptions}
                            filterOption={filterOptionByLabel}
                        />
                    </Form.Item>
                    <Form.Item name="dataPack" label="Gói cước"
                        style={{ flex: 1, minWidth: 200 }}
                        rules={[{ required: true, message: 'Vui lòng chọn gói cước!' }]}>
                        <Select
                            style={{ width: '100%' }}
                            showSearch
                            disabled={!cpCode || dataExcel?.length > 0}
                            options={partnerPackageOptions?.filter((item: any) => {
                                if (filterPack) {
                                    return item.label?.toLowerCase().includes(filterPack.toLowerCase())
                                }
                                if (item.label?.includes('_Gia hạn')) {
                                    return false
                                }
                                return true
                            })}
                            filterOption={filterOptionByLabel}
                        />
                    </Form.Item>
                    <Form.Item style={{ maxWidth: 200 }} label="Lọc gói cước">
                        <Input onChange={e => setFilterPack(e.target.value)} autoComplete="off" />
                    </Form.Item>
                </div>
                <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                    <Form.Item name='file' label="File Excel"
                        style={{ flex: 2, minWidth: 200 }}
                        tooltip={<div>
                            <p style={{ fontSize: 10 }}><b>Định dạng:</b> Msisdn, SerialSim </p>
                            <p style={{ fontSize: 10 }}><b>Tên sheet:</b> Sheet1, </p>
                            <p style={{ fontSize: 10 }}><b>Tối đa:</b> 1.000 sim</p>
                        </div>}
                        rules={[{ required: true, message: 'Vui lòng đăng tải file Excel!' }]}>
                        <Space style={{ display: 'flex' }}>
                            <Upload
                                maxCount={1}
                                onChange={handleChangeFile}
                                beforeUpload={checkUploadFile}
                                onRemove={() => {
                                    setDataExcel(null)
                                    setOrderAdded(null)
                                    setOrderDetail(null)
                                }}
                            >
                                <Button icon={<UploadOutlined />}>Upload</Button>
                            </Upload>
                        </Space>
                    </Form.Item>
                    {account?.canSelectDateConnect && (<>
                        <Form.Item>
                            <Tooltip title="Hiển thị ngày đấu nối">
                                <Checkbox onChange={() => {
                                    setIsOpenSelectDate(!isOpenSelectDate)
                                    formConnect.setFieldValue('startDate', '')
                                }} />
                            </Tooltip>
                        </Form.Item>
                        {isOpenSelectDate && (
                            <Form.Item name="startDate" label="Ngày đấu nối">
                                <DatePicker />
                            </Form.Item>
                        )}
                    </>
                    )}
                </div>
                <div style={{ textAlign: 'center' }}>
                    <Button htmlType="submit" icon={<EyeOutlined />} style={{ background: '#a21ccf', color: 'white' }}>
                        <b>Xem trước</b>
                    </Button>
                </div>
            </Form>
            <Divider />
            <CustomTable
                namePage='giao dịch'
                columns={excelColumns}
                dataSource={dataExcel?.slice((currentPagePreview - 1) * pageSizePreview, currentPagePreview * pageSizePreview) || []}
                total={dataExcel?.length}
                pageSize={pageSizePreview}
                setPageSize={setPageSizePreview}
                currentPage={currentPagePreview}
                setCurrentPage={setCurrentPagePreview}
            />
            <div style={{ textAlign: 'right', marginTop: 20 }}>
                {dataExcel && Number(dataExcel?.length || 0) > 0 && (
                    orderAdded?.orderId ? (
                        <Space>
                            {account?.canGetOrderTransactionConnectionDetail && (
                                <Tooltip title="Chi tiết đơn hàng">
                                    <Button
                                        onClick={fetchOrderDetail}
                                        icon={<EyeOutlined />}
                                        style={{ backgroundColor: '#52c41a', color: '#fff' }}
                                    >
                                        Chi tiết
                                    </Button>
                                </Tooltip>
                            )}
                            {account?.canAddTransactionConnectionByOrderId && (
                                <Tooltip title="Đấu nối đơn hàng">
                                    <Button
                                        disabled={orderAdded.status !== 'CREATED'}
                                        onClick={() => setModalOrderId({ data: orderAdded, isOpen: true })}
                                        icon={<MoneyCollectOutlined />}
                                        type='primary'
                                    >
                                        Đấu nối
                                    </Button>
                                </Tooltip>
                            )}
                            {account?.canUpdateOrderTransactionConnection && (
                                <Tooltip title="Khoá đơn hàng">
                                    <Button
                                        disabled={orderAdded.status === 'LOCKED' || orderAdded.status !== 'CREATED'}
                                        onClick={() => setModalLock(true)}
                                        icon={<BlockOutlined />}
                                        type='primary'
                                        danger
                                    >
                                        Khoá đơn
                                    </Button>
                                </Tooltip>
                            )}
                        </Space>
                    ) : (
                        <Button
                            onClick={handleUploadFile}
                            htmlType="submit" icon={<CheckOutlined />}
                            style={{ background: '#008965', color: 'white' }}
                        >
                            <b>Xác nhận</b>
                        </Button>
                    )
                )}
            </div>
            <Modal title='Đấu nối cho đơn hàng' open={modalOrderId.isOpen}
                onCancel={(() => setModalOrderId({ data: null, isOpen: false }))}
                footer={[
                    <Button onClick={handleAddTransactionConnectionByOrderId}
                        style={{ background: '#008965', color: 'white' }}>
                        Xác nhận
                    </Button>,
                ]}>
                <Divider />
                <p>Đấu nối cho đơn hàng <b>{modalOrderId.data?.orderId}</b></p>
                <Divider />
            </Modal>
            <Modal width={'740px'} title={`Chi tiết: ${orderAdded?.orderId}`} open={!!orderDetail}
                onCancel={(() => setOrderDetail(null))} footer={null}>
                {orderDetail?.transactions?.length > 0 && (
                    <Descriptions
                        items={[
                            {
                                key: 'dataPack',
                                label: 'Gói cước',
                                children: orderAdded.dataPack,
                            },
                            {
                                key: 'type',
                                label: 'Loại gói cước',
                                children: renderType(orderAdded.type),
                            },
                            {
                                key: 'startDate',
                                label: 'Ngày đấu nối',
                                children: renderDateByUnix(orderAdded.startDate),
                            },
                            {
                                key: 'total',
                                label: 'Tổng SIM',
                                children: orderDetail.total,
                            },
                            {
                                key: 'totalSuccess',
                                label: 'Tổng thành công',
                                children: orderDetail.transactions.reduce((acc: any, item: any) => item.status === 'SUCCESS' ? acc + 1 : acc, 0),
                            },
                            {
                                key: 'totalFail',
                                label: 'Tổng thất bại',
                                children: orderDetail.transactions.reduce((acc: any, item: any) => item.status === 'FAIL' ? acc + 1 : acc, 0),
                            },
                        ]}
                    />
                )}
                <CustomTable
                    namePage='giao dịch'
                    columns={orderDetailColumns}
                    dataSource={orderDetail?.transactions?.slice((currentPageDetail - 1) * pageSizeDetail, currentPageDetail * pageSizeDetail) || []}
                    pageSize={pageSizeDetail}
                    setPageSize={setPageSizeDetail}
                    total={orderDetail?.total}
                    currentPage={currentPageDetail}
                    setCurrentPage={setCurrentPageDetail}
                />
            </Modal>
            <Modal title='Khoá đơn hàng' open={modalLock}
                onCancel={(() => setModalLock(false))}
                footer={[
                    <Button onClick={() => handleLockOrderTransactionConnection({ id: orderAdded.id, orderId: orderAdded?.orderId, status: 'LOCKED' })}
                        type='primary' danger>
                        Xác nhận
                    </Button>,
                ]}>
                <Divider />
                <p>Khoá đơn hàng <b>{orderAdded?.orderId}</b></p>
                <Divider />
            </Modal>
        </div>
    )
}
